import React, { useCallback, useEffect, useState, useMemo } from 'react'
import {
  useForm,
  useNotifications,
  useFlashBackground
} from '../../../../hooks'
import { PanelSection } from '../../DispatchPanel'
import { AddressForm, LatLongForm } from '../../../LocationForm'
import Button from '../../../Button'
import TextAreaField from '../../../Form/TextAreaField'
import CheckboxField from '../../../Form/CheckboxField'
import Spinner from '../../../Spinner'
import { formatAddress } from '../../../LocationRenderer'
import { iconClassName } from '../../DispatchMap/icons'
import { USER_TYPE_FLAGS } from '../../../CreateUserType'
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore'
import { auth, db } from '../../../../firebase'
import './styles.scss'
import { useIdGenerator } from '../../../../hooks/useidgenerator'
import { sendNewIncidentNotification } from '../../../../hooks/notificationService'

function mapGeocodeAddress(geocodeResult) {
  const { address_components: components } = geocodeResult

  const streetComponent = components.find((c) => c.types[0] === 'route')
  const numberComponent = components.find((c) => c.types[0] === 'street_number')
  let cityComponent = components.find((c) => c.types[0] === 'locality')
  if (!cityComponent) {
    cityComponent = components.find(
      (c) => c.types[0] === 'administrative_area_level_2'
    )
  }

  const stateComponent = components.find(
    (c) => c.types[0] === 'administrative_area_level_1'
  )
  const zipComponent = components.find((c) => c.types[0] === 'postal_code')

  let street = ''
  if (numberComponent) {
    street += numberComponent.long_name + ' '
  }

  if (streetComponent) {
    street += streetComponent.long_name
  }

  return {
    street,
    city: (cityComponent && cityComponent.long_name) || '',
    state: (stateComponent && stateComponent.short_name) || '',
    zip: (zipComponent && zipComponent.long_name) || ''
  }
}

async function reverseGeocode(latitude, longitude) {
  const geocoder = new window.google.maps.Geocoder()
  const result = await new Promise((resolve, reject) => {
    geocoder.geocode(
      { location: { lng: longitude, lat: latitude } },
      (results, status) => {
        if (status !== 'OK' || results.length === 0) {
          reject(status)
          return
        }
        resolve(results[0])
      }
    )
  })

  const address = mapGeocodeAddress(result)
  return { address }
}

const ORGS_PREFIX = 'organization:'

function incidentNameFromDescription(description) {
  description = description.trim()

  const maxLength = 40
  if (description.length <= maxLength) {
    return description
  }

  let name = ''
  for (const word of description.split(' ')) {
    if (name.length + word.length > maxLength) {
      break
    }
    name += word + ' '
  }

  return name.slice(0, maxLength).trim()
}

export default function CreateIncidentPanel({
  mapSelection,
  userTypes,
  dispatchBindings,
  profile,
  onIncidentCreated,
  addressSearchBoxRef,
  flashAddressSearchBox
}) {
  const [getFieldProps, formData, formError, setFormData] = useForm()
  const [editing, setEditing] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const addNotification = useNotifications()
  const [error, setError] = useState(null)
  const [flashLocBgRef, flashLocBg] = useFlashBackground()
  const { generateIncidentId } = useIdGenerator()

  const setLookup = useCallback(() => {
    const el = addressSearchBoxRef.current
    if (el) {
      el.focus()
      flashAddressSearchBox()
    }
  }, [addressSearchBoxRef, flashAddressSearchBox])

  const userTypesById = useMemo(() => {
    if (!userTypes?.length) {
      console.warn('userTypes is missing or empty:', userTypes)
      return {}
    }

    return userTypes.reduce(
      (acc, userType) => ({
        ...acc,
        [userType.id]: userType
      }),
      {}
    )
  }, [userTypes])

  const dispatchOrgs = useMemo(() => {
    if (!userTypes?.length || !profile?.organization) return {}

    const dispatchableUserTypes = userTypes.filter(
      ({ flags }) => !!(flags & USER_TYPE_FLAGS.DISPATCHABLE)
    )

    // ✅ Always include current org + dispatch bindings
    const orgSet = new Map()

    // Always add current user's organization
    orgSet.set(profile.organization.id, profile.organization)

    // Add dispatch binding organizations
    if (dispatchBindings?.length) {
      for (const { dispatchOrganization } of dispatchBindings) {
        if (dispatchOrganization) {
          orgSet.set(dispatchOrganization.id, dispatchOrganization)
        }
      }
    }

    // Convert to array
    const orgsArray = Array.from(orgSet.values())

    // ✅ Group by userType — but keep current organization
    return orgsArray.reduce((acc, organization) => {
      const userTypeId =
        organization.defaultUserType?.id ||
        profile.userType?.id || // fallback for user org without defaultUserType
        'unknown'

      // Ensure it's allowed or is current org
      const isDispatchable = dispatchableUserTypes.some(
        (ut) => ut.id == userTypeId
      )
      const isCurrentOrg = organization.id === profile.organization.id

      if (isDispatchable || isCurrentOrg) {
        if (!acc[userTypeId]) acc[userTypeId] = []
        acc[userTypeId].push(organization)
      }

      return acc
    }, {})
  }, [userTypes, dispatchBindings, profile])

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault()
      setError(null)

      let { description, latitude, longitude, street, city, state, zip } =
        formData

      if (!latitude || !longitude) {
        flashLocBg()
        return
      }

      latitude = parseFloat(latitude)
      if (Number.isNaN(latitude)) {
        formError({ latitude: 'Invalid value.' })
        return
      }

      longitude = parseFloat(longitude)
      if (Number.isNaN(longitude)) {
        formError({ longitude: 'Invalid value.' })
        return
      }

      const dispatchOrganizationIds = Object.entries(formData)
        .filter(([key, value]) => key.startsWith(ORGS_PREFIX) && value)
        .map(([key]) => key.slice(ORGS_PREFIX.length))

      if (dispatchOrganizationIds.length === 0) {
        setError({ dispatchOrganizations: true })
        return
      }

      // const dispatchOrganizations = dispatchOrganizationIds.map((id) => ({
      //   id,
      //   name: org?.name,
      //   offDuty: formData[`offduty:${ORGS_PREFIX}${id}`] || false
      // }));

      const dispatchOrganizations = dispatchOrganizationIds.map((id) => {
        // Find the organization in any of the dispatchOrgs groups
        let org = null
        for (const orgs of Object.values(dispatchOrgs)) {
          org = orgs.find((o) => o.id == id)
          if (org) break
        }

        return {
          id,
          name: org?.name,
          offDuty: formData[`offduty:${ORGS_PREFIX}${id}`] || false,
          createdAt: org?.createdAt || new Date().toISOString()
        }
      })

      // const dispatchOrganizations = dispatchOrganizationIds.map((id) => {
      // // Find the organization in any of the dispatchOrgs groups
      //   let org = null;
      //   for (const orgs of Object.values(dispatchOrgs)) {
      //     org = orgs.find(o => o.id === id);
      //     if (org) break;
      //   }

      //   return {
      //     id,
      //     name: org?.name || 'Unknown Organization',
      //     offDuty: formData[`offduty:${ORGS_PREFIX}${id}`] || false,
      //     createdAt: org?.createdAt || new Date().toISOString()
      //   };
      // });

      setEditing(false)
      setSubmitting(true)

      const locations = []

      if (latitude && longitude) {
        locations.push({
          format: 'lat-long',
          latitude,
          longitude
        })
      }

      const hasAddress = street || city || state || zip
      if (hasAddress) {
        locations.push({
          format: 'us-address',
          street,
          city,
          state,
          zip
        })
      }

      try {
        const incidentId = generateIncidentId()
        if (!profile || !profile.id) {
          throw new Error('User profile information is missing')
        }

        let notificationUserList = []
        for (const orgId of dispatchOrganizationIds) {
          const q = query(
            collection(db, 'users'),
            where('organization.id', '==', Number(orgId))
          )
          const snap = await getDocs(q)
          snap.forEach((doc) => {
            const data = doc.data()
            if (data.id) {
              notificationUserList.push(data.id)
            }
          })
        }
        // Remove duplicates
        notificationUserList = [...new Set(notificationUserList)]

        const incident = {
          id: incidentId,
          name: incidentNameFromDescription(description),
          description,
          status: 'pending',
          locations,
          dispatchOrganizations,
          dispatchOrganizationsIdList: dispatchOrganizationIds,
          createdAt: new Date().toISOString(),
          createdBy: profile.id,
          // user: { id: profile.id, personalData: profile.personalData },
          dispatch: {},
          acknowledgements: [],
          mapMarkers: [],
          customData: [],
          organization: {
            id: profile.organization.id,
            name:
              profile.organization.name ||
              `Organization ${profile.organization.id}`
          },
          notificationUserList
          // userType: {
          //   id: profile.userType.id,
          //   name: profile.userType.name || `User Type ${profile.userType.id}`,
          //   icon: profile.userType.icon || 'default',
          //   color: profile.userType.color || '#000',
          //   flags: profile.userType.flags || 0
          // }
        }

        const incidentRef = doc(db, 'incidents', incidentId) // Use custom ID
        await setDoc(incidentRef, incident) // Set document with custom ID
        const newIncident = { ...incident, id: incidentId }

        // const incidentRef = await addDoc(collection(db, 'incidents'), incident);
        // await updateDoc(incidentRef, {
        //   id: incidentRef.id
        // });
        // const newIncident = { ...incident, id: incidentRef.id };

        addNotification(
          <div>
            <div>New incident launched.</div>
            <div>
              <strong>{incident.name}</strong>
            </div>
            {hasAddress && <div>{formatAddress(formData)}</div>}
          </div>,
          { className: 'is-success' }
        )

        for (const org of dispatchOrganizations) {
          try {
            await sendNewIncidentNotification(newIncident, org, org.offDuty)
          } catch (notificationError) {
            console.error(
              `Failed to send notification for org ${org.id}:`,
              notificationError
            )
            // Don't fail the whole operation if notifications fail
            addNotification(
              <div>
                <div>Notification failed for {org.name}</div>
                <div>
                  <small>
                    Incident was created but notification failed to send
                  </small>
                </div>
              </div>,
              { className: 'is-warning', autoDismiss: true }
            )
          }
        }

        onIncidentCreated(newIncident)
      } catch (err) {
        setSubmitting(false)
        if (!editing) {
          setEditing(true)
        }
        if (err.message.includes('validation')) {
          formError({ description: 'Invalid incident name.' })
        } else {
          setError(err)
        }
      }
    },
    [
      editing,
      formData,
      formError,
      setError,
      addNotification,
      onIncidentCreated,
      flashLocBg,
      profile,
      dispatchOrgs
    ]
  )

  const clearLocation = useCallback(() => {
    setFormData(null)
    setEditing(false)
  }, [setFormData, setEditing])

  useEffect(() => {
    if (!mapSelection) {
      return
    }

    const [longitude, latitude] = mapSelection.loc

    reverseGeocode(latitude, longitude)
      .then(({ address }) =>
        setFormData((formData) => ({
          ...formData,
          ...address,
          latitude,
          longitude
        }))
      )
      .catch((err) => {
        console.error(err)
        setFormData((formData) => ({
          ...formData,
          latitude,
          longitude
        }))
      })
  }, [mapSelection])

  return (
    <form style={{ width: '100%' }} onSubmit={handleSubmit}>
      <PanelSection title='1. Location'>
        {(!formData || !formData.latitude) && !editing && (
          <div ref={flashLocBgRef} className='location-select'>
            <div className='location-option' onClick={() => setLookup()}>
              <i className='fa fa-search' />
              <div>Lookup</div>
            </div>
            <div className='location-option'>
              <i className='fa fa-map-marker-alt' />
              <div>Click on map</div>
            </div>
            <div className='location-option' onClick={() => setEditing(true)}>
              <i className='fa fa-keyboard' />
              <div>Custom</div>
            </div>
          </div>
        )}
        {formData && formData.latitude && mapSelection && !editing && (
          <div>
            <div className='location-address'>{formatAddress(formData)}</div>
            <div className='location-latlng'>
              {formData.latitude.toFixed(8)}, {formData.longitude.toFixed(8)}{' '}
              (Lat., Lon.)
            </div>
            {!submitting && (
              <div className='location-menu'>
                <button
                  type='button'
                  className='button is-text'
                  onClick={() => setEditing(true)}
                  style={{
                    color: '#7787D4',
                    fontWeight: 400
                  }}>
                  Edit
                </button>
                <button
                  type='button'
                  className='button is-text'
                  onClick={() => clearLocation()}
                  style={{
                    color: '#7787D4',
                    fontWeight: 400
                  }}>
                  Clear location
                </button>
                <div
                  style={{
                    fontSize: '0.65rem',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#7787D4',
                    fontWeight: 400
                  }}>
                  Or click the map to move the marker.
                </div>
              </div>
            )}
          </div>
        )}
        {editing && (
          <>
            <LatLongForm
              getFieldProps={getFieldProps}
              defaultValue={formData}
            />
            <AddressForm
              getFieldProps={getFieldProps}
              defaultValue={formData}
            />
            <button
              type='button'
              className='button is-text'
              onClick={() => clearLocation()}
              style={{
                color: '#7787D4',
                fontWeight: 400
              }}>
              Clear location
            </button>
          </>
        )}
      </PanelSection>
      <PanelSection title='2. Description'>
        <div className='field'>
          <label className='label has-text-info'>Name</label>
          <div className='control'>
            <div className='field'>
              <div className='control' style={{ fontSize: '0.9em' }}>
                {formData && incidentNameFromDescription(formData.description)}
              </div>
            </div>
          </div>
        </div>
        <TextAreaField
          name='description'
          label='Description'
          required={true}
          readOnly={submitting}
          {...getFieldProps()}
        />
      </PanelSection>
      <PanelSection
        title='3. Send to'
        className='dispatch-type-select dispatch-types-select-send'>
        {Object.entries(dispatchOrgs).length > 0 ? (
          Object.entries(dispatchOrgs)?.map(([userTypeId, organizations]) => (
            <div className='dispatch-type-select-item' key={userTypeId}>
              <div className='dispatch-type-select-item-body'>
                <div className='dispatch-type-select-item-header'>
                  <div className='dispatch-type-select-item-header-icon'>
                    <i
                      className={iconClassName(
                        userTypesById[userTypeId]?.icon || 'default'
                      )}
                      style={{
                        color: userTypesById[userTypeId]?.color || '#000'
                      }}
                    />
                  </div>
                  <span>
                    {userTypesById[userTypeId]?.name || 'Unknown Type'}
                  </span>
                </div>
                {organizations?.map(({ id, name }) => (
                  <div key={id}>
                    <CheckboxField
                      name={`${ORGS_PREFIX}${id}`}
                      label={name || 'Unknown Organization'}
                      disabled={submitting}
                      defaultChecked={id === profile?.organization?.id}
                      {...getFieldProps()}
                    />
                    {formData && formData[`${ORGS_PREFIX}${id}`] && (
                      <div style={{ marginLeft: '1.5rem' }}>
                        <CheckboxField
                          name={`offduty:${ORGS_PREFIX}${id}`}
                          label='Include Off-Duty'
                          disabled={submitting}
                          defaultChecked={false}
                          {...getFieldProps()}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div>No dispatchable organizations available.</div>
        )}
      </PanelSection>
      <div style={{ marginTop: '1em' }}>
        {error && (
          <div className='notification is-warning'>
            {error.dispatchType && 'Please select at least one dispatch type.'}
            {error.dispatchOrganizations &&
              'Please select at least one dispatch organization.'}
            {error.fetchError && error.fetchError}
          </div>
        )}
        <Button
          type='submit'
          className='is-primary'
          style={{ float: 'right', marginRight: '1em' }}
          disabled={submitting}>
          {!submitting && 'Launch incident'}
          {submitting && (
            <span>
              <Spinner /> Creating
            </span>
          )}
        </Button>
      </div>
    </form>
  )
}
